/**
 * Permission System for Role-Based Access Control
 */

export enum Permission {
  // User Management
  USER_CREATE = 'user:create',
  USER_READ = 'user:read',
  USER_UPDATE = 'user:update',
  USER_DELETE = 'user:delete',
  USER_ROLE_ASSIGN = 'user:role:assign',
  USER_SUSPEND = 'user:suspend',
  USER_IMPERSONATE = 'user:impersonate',
  
  // Monitoring
  MONITORING_VIEW = 'monitoring:view',
  MONITORING_MANAGE = 'monitoring:manage',
  MONITORING_ALERTS = 'monitoring:alerts',
  
  // Billing
  BILLING_VIEW = 'billing:view',
  BILLING_MANAGE = 'billing:manage',
  BILLING_REPORTS = 'billing:reports',
  BILLING_REFUNDS = 'billing:refunds',
  
  // Support
  SUPPORT_VIEW = 'support:view',
  SUPPORT_RESPOND = 'support:respond',
  SUPPORT_ASSIGN = 'support:assign',
  SUPPORT_CLOSE = 'support:close',
  SUPPORT_VIEW_TICKETS = 'support:view_tickets',
  SUPPORT_MANAGE_TICKETS = 'support:manage_tickets',
  SUPPORT_ESCALATE_TICKETS = 'support:escalate_tickets',
  SUPPORT_VIEW_FEEDBACK = 'support:view_feedback',
  SUPPORT_RESPOND_FEEDBACK = 'support:respond_feedback',
  SUPPORT_VIEW_KB = 'support:view_kb',
  SUPPORT_MANAGE_KB = 'support:manage_kb',
  
  // Analytics
  ANALYTICS_VIEW = 'analytics:view',
  ANALYTICS_EXPORT = 'analytics:export',
  ANALYTICS_CUSTOM = 'analytics:custom',
  
  // System
  SYSTEM_SETTINGS = 'system:settings',
  SYSTEM_AUDIT = 'system:audit',
  SYSTEM_INTEGRATIONS = 'system:integrations',
  SYSTEM_BACKUP = 'system:backup',
  SYSTEM_API_KEYS = 'system:api_keys',
  
  // Onboarding
  ONBOARDING_VIEW = 'onboarding:view',
  ONBOARDING_MANAGE = 'onboarding:manage',
  
  // Sales & Leads
  LEAD_CREATE = 'lead:create',
  LEAD_READ = 'lead:read',
  LEAD_UPDATE = 'lead:update',
  LEAD_DELETE = 'lead:delete',
  LEAD_ASSIGN = 'lead:assign',
  
  // Demos
  DEMO_CREATE = 'demo:create',
  DEMO_READ = 'demo:read',
  DEMO_UPDATE = 'demo:update',
  DEMO_DELETE = 'demo:delete',
  
  // Proposals
  PROPOSAL_CREATE = 'proposal:create',
  PROPOSAL_READ = 'proposal:read',
  PROPOSAL_UPDATE = 'proposal:update',
  PROPOSAL_SEND = 'proposal:send',
  
  // Contracts
  CONTRACT_CREATE = 'contract:create',
  CONTRACT_READ = 'contract:read',
  CONTRACT_UPDATE = 'contract:update',
  CONTRACT_DELETE = 'contract:delete',
  
  // Revenue & Pipeline
  REVENUE_VIEW = 'revenue:view',
  REVENUE_REPORTS = 'revenue:reports',
  PIPELINE_VIEW = 'pipeline:view',
  PIPELINE_MANAGE = 'pipeline:manage',
  
  // Customer Management
  CUSTOMER_CREATE = 'customer:create',
  CUSTOMER_READ = 'customer:read',
  CUSTOMER_UPDATE = 'customer:update',
  CUSTOMER_HEALTH_VIEW = 'customer:health:view',
  
  // Properties Management
  PROPERTY_VIEW = 'property:view',
  PROPERTY_MANAGE = 'property:manage',
  
  // Assets Management
  ASSET_VIEW = 'asset:view',
  ASSET_MANAGE = 'asset:manage',
  
  // Reports
  REPORT_VIEW = 'report:view',
  REPORT_GENERATE = 'report:generate',
  REPORT_EXPORT = 'report:export',
}

/**
 * Internal Admin Roles (for internal admins only, NOT portal users)
 * These roles have access to the admin dashboard
 */
export type AdminRole = 
  | 'superadmin'    // Full system access
  | 'admin'         // Administrative access
  | 'sales'         // Sales team
  | 'support'       // Support team
  | 'accountant';   // Financial/accounting access

/**
 * Legacy role mapping for backward compatibility during migration
 * 'landlord' users in old system should be treated as portal 'property_owner'
 */
export type UserRole = AdminRole | 'landlord'; // landlord kept for backward compat during migration

/**
 * Role Permission Mapping
 * Defines what permissions each admin role has access to
 */
export const ROLE_PERMISSIONS: Record<AdminRole, Permission[]> = {
  superadmin: Object.values(Permission), // All permissions
  
  admin: [
    // User Management (except role assignment)
    Permission.USER_READ,
    Permission.USER_UPDATE,
    Permission.USER_SUSPEND,
    
    // Monitoring
    Permission.MONITORING_VIEW,
    Permission.MONITORING_MANAGE,
    Permission.MONITORING_ALERTS,
    
    // Billing
    Permission.BILLING_VIEW,
    Permission.BILLING_MANAGE,
    Permission.BILLING_REPORTS,
    
    // Support (full access)
    Permission.SUPPORT_VIEW,
    Permission.SUPPORT_RESPOND,
    Permission.SUPPORT_ASSIGN,
    Permission.SUPPORT_CLOSE,
    Permission.SUPPORT_VIEW_TICKETS,
    Permission.SUPPORT_MANAGE_TICKETS,
    Permission.SUPPORT_ESCALATE_TICKETS,
    Permission.SUPPORT_VIEW_FEEDBACK,
    Permission.SUPPORT_RESPOND_FEEDBACK,
    Permission.SUPPORT_VIEW_KB,
    Permission.SUPPORT_MANAGE_KB,
    
    // Analytics
    Permission.ANALYTICS_VIEW,
    Permission.ANALYTICS_EXPORT,
    
    // System
    Permission.SYSTEM_SETTINGS,
    Permission.SYSTEM_INTEGRATIONS,
    
    // Onboarding
    Permission.ONBOARDING_VIEW,
    Permission.ONBOARDING_MANAGE,
    
    // Properties & Assets
    Permission.PROPERTY_VIEW,
    Permission.PROPERTY_MANAGE,
    Permission.ASSET_VIEW,
    Permission.ASSET_MANAGE,
    
    // Reports
    Permission.REPORT_VIEW,
    Permission.REPORT_GENERATE,
    Permission.REPORT_EXPORT,
  ],
  
  sales: [
    // Leads
    Permission.LEAD_CREATE,
    Permission.LEAD_READ,
    Permission.LEAD_UPDATE,
    Permission.LEAD_ASSIGN,
    
    // Demos
    Permission.DEMO_CREATE,
    Permission.DEMO_READ,
    Permission.DEMO_UPDATE,
    
    // Proposals
    Permission.PROPOSAL_CREATE,
    Permission.PROPOSAL_READ,
    Permission.PROPOSAL_UPDATE,
    Permission.PROPOSAL_SEND,
    
    // Contracts (read-only)
    Permission.CONTRACT_READ,
    
    // Customer (read-only)
    Permission.CUSTOMER_READ,
    
    // Revenue & Pipeline
    Permission.REVENUE_VIEW,
    Permission.PIPELINE_VIEW,
    
    // Analytics (view only)
    Permission.ANALYTICS_VIEW,
  ],
  
  support: [
    // User Management (read-only)
    Permission.USER_READ,
    
    // Monitoring (read-only)
    Permission.MONITORING_VIEW,
    
    // Support (full access)
    Permission.SUPPORT_VIEW,
    Permission.SUPPORT_RESPOND,
    Permission.SUPPORT_ASSIGN,
    Permission.SUPPORT_VIEW_TICKETS,
    Permission.SUPPORT_MANAGE_TICKETS,
    Permission.SUPPORT_ESCALATE_TICKETS,
    Permission.SUPPORT_VIEW_FEEDBACK,
    Permission.SUPPORT_RESPOND_FEEDBACK,
    Permission.SUPPORT_VIEW_KB,
    Permission.SUPPORT_MANAGE_KB,
    
    // Analytics (limited)
    Permission.ANALYTICS_VIEW,
    
    // Onboarding (view only)
    Permission.ONBOARDING_VIEW,
    
    // Properties & Assets (view only)
    Permission.PROPERTY_VIEW,
    Permission.ASSET_VIEW,
    
    // Reports (view only)
    Permission.REPORT_VIEW,
  ],
  
  accountant: [
    // User Management (read-only)
    Permission.USER_READ,
    
    // Billing
    Permission.BILLING_VIEW,
    Permission.BILLING_REPORTS,
    
    // Analytics
    Permission.ANALYTICS_VIEW,
    Permission.ANALYTICS_EXPORT,
  ],
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  if (role === 'landlord') return false; // Landlord has no admin permissions
  const permissions = ROLE_PERMISSIONS[role as AdminRole];
  return permissions?.includes(permission) ?? false;
}

/**
 * Check if a role has any of the specified permissions
 */
export function hasAnyPermission(role: UserRole, permissions: Permission[]): boolean {
  return permissions.some(p => hasPermission(role, p));
}

/**
 * Check if a role has all of the specified permissions
 */
export function hasAllPermissions(role: UserRole, permissions: Permission[]): boolean {
  return permissions.every(p => hasPermission(role, p));
}

/**
 * Get all permissions for a role
 */
export function getPermissions(role: UserRole): Permission[] {
  if (role === 'landlord') return [];
  return ROLE_PERMISSIONS[role as AdminRole] || [];
}

/**
 * Check if user can access admin dashboard
 */
export function canAccessAdminDashboard(role: UserRole): boolean {
  return role !== 'landlord';
}

/**
 * Check if a role is an admin role
 */
export function isAdminRole(role: UserRole | string): role is AdminRole {
  return ['superadmin', 'admin', 'sales', 'support', 'accountant'].includes(role as string);
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role: UserRole): string {
  const names: Record<UserRole, string> = {
    superadmin: 'Super Administrator',
    admin: 'Administrator',
    sales: 'Sales Representative',
    support: 'Support Agent',
    accountant: 'Accountant',
    landlord: 'Landlord (Portal User)',
  };
  return names[role];
}

/**
 * Get role description
 */
export function getRoleDescription(role: UserRole): string {
  const descriptions: Record<UserRole, string> = {
    superadmin: 'Full system access including user management and role assignment',
    admin: 'Full dashboard access with billing, support, and system management',
    sales: 'Sales pipeline management with lead, demo, and proposal access',
    support: 'Customer support access with ticket management and monitoring',
    accountant: 'Financial reports and billing access only',
    landlord: 'Portal access for property and tenant management (not admin)',
  };
  return descriptions[role];
}

/**
 * Get role badge color
 */
export function getRoleBadgeColor(role: UserRole): string {
  const colors: Record<UserRole, string> = {
    superadmin: 'purple',
    admin: 'blue',
    sales: 'cyan',
    support: 'green',
    accountant: 'orange',
    landlord: 'gray',
  };
  return colors[role];
}

/**
 * Role display information
 */
export const ROLE_DISPLAY: Record<UserRole, { name: string; description: string; color: string }> = {
  superadmin: {
    name: 'Superadmin',
    description: 'Full system access including user management and role assignment',
    color: 'purple',
  },
  admin: {
    name: 'Admin',
    description: 'Full dashboard access with billing, support, and system management',
    color: 'blue',
  },
  sales: {
    name: 'Sales',
    description: 'Sales pipeline management with lead, demo, and proposal access',
    color: 'cyan',
  },
  support: {
    name: 'Support',
    description: 'Customer support access with ticket management and monitoring',
    color: 'green',
  },
  accountant: {
    name: 'Accountant',
    description: 'Financial reports and billing access only',
    color: 'orange',
  },
  landlord: {
    name: 'Landlord',
    description: 'Portal user for property and tenant management (not admin dashboard)',
    color: 'gray',
  },
};
